import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import {
    AddRemoveEducationReminder,
    Education,
    EducationsRequest,
    EducationsResponse,
    FarmerObligationsResponse,
    GetEducationsByTitlesAndCountyResponse,
    RegistryResponse,
    addRemoveEducationReminder,
    getAllEducations,
    getEducation,
    getEducationsByTitlesAndCounty,
    getFarmerObligations,
    getRegistries,
    getRegistriesByPgs,
    getUserEducationsWithReminders,
    getEducationStatus,
} from "services";
import { useAccessToken } from "./useAccessToken";
import { useStandardErrorHandler } from "./useStandardErrorHandler";

export const useGetRegistries = (enabled = true) => {
    const accessToken = useAccessToken();
    const errorHandler = useStandardErrorHandler("Greška pri dohvaćanju registara");

    return useQuery<AxiosResponse<{ registries: RegistryResponse[] }>, Error, RegistryResponse[]>({
        queryKey: ["registries"],
        queryFn: () => getRegistries(accessToken!),
        enabled: enabled,
        onError: errorHandler,
        select: response => response.data.registries,
    });
};

export const useGetRegistriesByPgs = (mibpg: string, enabled = true) => {
    const accessToken = useAccessToken();
    const errorHandler = useStandardErrorHandler("Greška pri dohvaćanju registara");

    return useQuery<AxiosResponse, Error, Record<string, RegistryResponse[]>>({
        queryKey: ["registriesByPgs", mibpg],
        queryFn: () => getRegistriesByPgs(mibpg, accessToken!),
        enabled: enabled,
        onError: errorHandler,
        select: response => response.data.registriesByPgs,
    });
};

export const useGetEducations = (data: EducationsRequest, enabled = true) => {
    const accessToken = useAccessToken();
    const errorHandler = useStandardErrorHandler("Greška pri dohvaćanju edukacija");

    return useQuery<AxiosResponse<EducationsResponse>, Error, EducationsResponse>({
        queryKey: ["educations", data],
        queryFn: () => getAllEducations(data, accessToken!),
        enabled: enabled,
        onError: errorHandler,
        select: response => response.data,
    });
};

export const useGetEducation = (educationId: string, enabled = true) => {
    const accessToken = useAccessToken();
    const errorHandler = useStandardErrorHandler("Greška pri dohvaćanju edukacije");

    return useQuery<AxiosResponse<{ education: Education }>, Error, Education>({
        queryKey: ["education", educationId],
        queryFn: () => getEducation(educationId, accessToken!),
        enabled: enabled,
        onError: errorHandler,
        select: response => response.data.education,
    });
};

export const useGetUserEducationsWithReminders = (enabled = true) => {
    const accessToken = useAccessToken();
    const errorHandler = useStandardErrorHandler(
        "Greška pri dohvaćanju edukacije sa podsjetnicima"
    );

    return useQuery<AxiosResponse<EducationsResponse>, Error, EducationsResponse>({
        queryKey: ["educationsWithReminders"],
        queryFn: () => getUserEducationsWithReminders(accessToken!),
        enabled: enabled,
        onError: errorHandler,
        select: response => response.data,
    });
};

export const useAddRemoveEdicationReminder = () => {
    const accessToken = useAccessToken();
    const queryClient = useQueryClient();
    const errorHandler = useStandardErrorHandler("Greška pri ažuriranju podsjetnika");

    return useMutation({
        mutationFn: async (data: AddRemoveEducationReminder) =>
            await addRemoveEducationReminder(data, accessToken!),
        onError: errorHandler,
        onSuccess: () => {
            queryClient.invalidateQueries(["education"]);
            queryClient.invalidateQueries(["educations"]);
            queryClient.invalidateQueries(["educationsWithReminders"]);
        },
    });
};

export const useGetFarmerObligations = (farmerId?: number[], enabled = true) => {
    const accessToken = useAccessToken();
    const errorHandler = useStandardErrorHandler("Greška pri dohvaćanju obaveza");

    return useQuery<AxiosResponse<FarmerObligationsResponse>, Error, FarmerObligationsResponse>({
        queryKey: ["farmerObligations"],
        queryFn: () => getFarmerObligations(farmerId!, accessToken!),
        enabled: Boolean(farmerId) && enabled,
        onError: errorHandler,
        select: response => response.data,
    });
};

export const useGetEducationsByTitlesAndCounty = (
    titles: string[],
    county?: string,
    enabled = true
) => {
    const accessToken = useAccessToken();
    const errorHandler = useStandardErrorHandler("Greška pri dohvaćanju edukacija");

    return useQuery<AxiosResponse, Error, GetEducationsByTitlesAndCountyResponse>({
        queryKey: ["educationsByTitlesAndCounty", titles, county],
        queryFn: () => getEducationsByTitlesAndCounty(titles, county!, accessToken!),
        enabled: titles.length > 0 && Boolean(county) && enabled,
        onError: errorHandler,
        select: response => response.data,
    });
};

export const useGetEducationStatus = (
    educationId?: number,
    statusId?: number,
    mibpg?: string,
    enabled = true
) => {
    const accessToken = useAccessToken();
    const errorHandler = useStandardErrorHandler("Greška pri dohvaćanju statusa edukacije");

    return useQuery<AxiosResponse<any>, Error, any>({
        queryKey: ["educationStatus", educationId, statusId],
        queryFn: () => getEducationStatus(educationId!, statusId!, mibpg!, accessToken!),
        enabled: Boolean(educationId) && typeof statusId === "number" && enabled,
        onError: errorHandler,
        select: response => response.data,
    });
};
